import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { add, differenceInWeeks, format, parseJSON, setHours, setMinutes } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { Box, Button, CheckBox, DateInput, FormField, Grid, MaskedInput, ResponsiveContext, Select, Text, TextInput } from "grommet";
import * as React from "react";
import { DateTimePicker, FormatDateTime, timeZone } from "../components/DateTimePicker";
import DaySelector, { DaysArray } from "../components/DaySelector";
import PlusMinus from "../components/PlusMinus";
import StyledModal from "../components/StyledModel";
import ThemeBuilder from "../components/ThemeBuilder";
import { useReferenceData } from "../reference/ReferenceStore";
import SeasonLogoPicker from "./SeasonLogoPicker";
import SeasonSplashPicker from "./SeasonSplashPicker";
import { SeasonViewModel } from "./SeasonsStore";

const SeasonDetails = ({ season, isBusy, onChange }: { season: SeasonViewModel; isBusy: boolean; onChange: Function }) => {
    const [name, setName] = React.useState(season.name || "");

    const [logoUri, setLogoUri] = React.useState(season.logoUri || "");
    const [splashUri, setSplashUri] = React.useState(season.splashUri || "");

    const [color, setColor] = React.useState(season.color || "");

    const [logoContent, setLogoContent] = React.useState("");
    const [splashContent, setSplashContent] = React.useState("");

    const [scheduleType, setScheduleType] = React.useState(season.scheduleType || "RoundRobin");

    const seasonScheduleTypes = useReferenceData("seasonScheduleTypes");

    let [startDate, setStartDate] = React.useState(season.startDate);
    let [endDate, setEndDate] = React.useState(season.endDate);
    let [weeks, setWeeks] = React.useState(differenceInWeeks(parseJSON(endDate), parseJSON(startDate)));

    React.useEffect(() => {
        onChange({ name, logoUri, logoContent, splashUri, splashContent, color, startDate, endDate, scheduleType });
    }, [name, logoUri, logoContent, splashUri, splashContent, color, startDate, endDate, scheduleType]);

    const size = React.useContext(ResponsiveContext);

    React.useEffect(() => {
        if (season.logoUri && logoUri != season.logoUri) {
            setLogoUri(season.logoUri);
        }
        if (season.splashUri && splashUri != season.splashUri) {
            setSplashUri(season.splashUri);
        }
    }, [season.logoUri, season.splashUri]);

    const handleLogoChange = ({ uri, content }) => {
        if (content) {
            setLogoContent(content);
            setLogoUri("");
        } else if (uri) {
            setLogoUri(uri);
            setLogoContent("");
        }
    };

    const handleSplashChange = ({ uri, content }) => {
        if (content) {
            setSplashContent(content);
            setSplashUri("");
        } else if (uri) {
            setSplashUri(uri);
            setSplashContent("");
        }
    };

    React.useEffect(() => {
        if (weeks > 0) {
            setEndDate(utcToZonedTime(add(parseJSON(startDate), { weeks }), timeZone).toISOString());
        }
    }, [weeks]);

    const [showDateEdit, setShowDateEdit] = React.useState(false);

    return (
        <div>
            <>
                <Box>
                    <Grid columns={size == "small" ? ["auto"] : ["1/2", "1/2"]} gap="small">
                        <Box>
                            <FormField label="Name">
                                <TextInput value={name} onChange={(e) => setName(e.target.value)} disabled={isBusy} />
                            </FormField>
                        </Box>

                        <FormField label="Schedule Type">
                            <Select
                                value={scheduleType}
                                onChange={(e) => setScheduleType(e.target.value)}
                                labelKey="displayName"
                                disabled={season.locked || isBusy}
                                options={seasonScheduleTypes}
                                children={(option, index, options, { active, disabled, selected }) => (
                                    <Box pad="1rem">
                                        <Text>{option.displayName}</Text>
                                    </Box>
                                )}
                                valueKey={{ key: "name", reduce: true }}
                            />
                        </FormField>

                        <Box border="bottom">
                            <Text margin={{ left: "small" }}>Start Date</Text>
                            <Text margin={{ left: "small" }}>{FormatDateTime(parseJSON(season.startDate), "yyyy/MM/dd")}</Text>
                        </Box>
                        <Box>
                            <Text margin={{ left: "small" }}>End Date</Text>
                            <Box direction="row" justify="between" border="bottom">
                                <Text margin={{ left: "small" }}>{FormatDateTime(parseJSON(season.endDate), "yyyy/MM/dd")}</Text>
                                <Box hoverIndicator="background-front" onClick={() => setShowDateEdit(true)} pad="small">
                                    <FontAwesomeIcon icon={["fad", "edit"]}></FontAwesomeIcon>
                                </Box>
                            </Box>
                        </Box>

                        <FormField label="Duration">
                            <Box pad="small" direction="row" justify="between">
                                <Text size="16px">{differenceInWeeks(parseJSON(season.endDate), parseJSON(season.startDate))} Weeks</Text>
                                {!(isBusy || season.locked) && <PlusMinus value={weeks} onChange={(w) => setWeeks(w)}></PlusMinus>}
                            </Box>
                        </FormField>
                        <Box></Box>

                        <Box fill={true}>
                            <SeasonLogoPicker uri={logoUri} onSelect={handleLogoChange}></SeasonLogoPicker>
                        </Box>
                        <Box fill={true}>
                            <SeasonSplashPicker uri={splashUri} onSelect={handleSplashChange}></SeasonSplashPicker>
                        </Box>
                    </Grid>
                    <ThemeBuilder uri={logoUri} color={color} onSelect={(color) => setColor(color)}></ThemeBuilder>
                </Box>
                <StyledModal isOpen={showDateEdit} setIsOpen={setShowDateEdit}>
                    <Box>
                        <Text margin={{ left: "small" }}>Start Date</Text>
                        <DateTimePicker value={season.startDate} onChange={setStartDate} disabled={isBusy || season.locked}></DateTimePicker>
                    </Box>
                    <Box>
                        <Text margin={{ left: "small" }}>End Date</Text>
                        <DateTimePicker value={season.endDate} onChange={setEndDate} disabled={isBusy || season.locked}></DateTimePicker>
                    </Box>
                </StyledModal>
            </>
        </div>
    );
};

export default SeasonDetails;
